import {useEffect, useRef, useState} from "react";
import {type GraphQLQuery} from "@aws-amplify/api-graphql";
import {useAmplifyClients} from "../../../amplify";
import {Tenant, TenantByIdQuery} from "../../../amplify/graphql";
import {tenantById as tenantByIdQuery} from "../../../amplify/graphql";

interface UseTenanciesResult {
  tenants: Tenant[];
  loading: boolean;
  error: Error | null;
}

export function useTenancies(tenantIds: string[]): UseTenanciesResult {
  const clients = useAmplifyClients();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(tenantIds.length > 0);
  const [error, setError] = useState<Error | null>(null);

  // Stabilize the array by content so the effect doesn't re-fire when the caller
  // passes a new array reference with the same IDs (tenant IDs are opaque strings
  // and will not contain commas).
  const currentKey = tenantIds.join(",");
  const stableIdsRef = useRef(tenantIds);
  const prevKeyRef = useRef(currentKey);
  if (currentKey !== prevKeyRef.current) {
    prevKeyRef.current = currentKey;
    stableIdsRef.current = tenantIds;
  }

  useEffect(() => {
    const ids = stableIdsRef.current;

    if (ids.length === 0) {
      setTenants([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const client = clients?.appsyncBff;
    if (!client) {
      setError(new Error("Amplify client not initialized"));
      setLoading(false);
      return;
    }

    Promise.all(
      ids.map(id =>
        client
          .graphql<GraphQLQuery<TenantByIdQuery>>({
            query: tenantByIdQuery,
            variables: {id}
          })
          .then(result => result.data?.tenantById ?? null)
          .catch(() => null)
      )
    )
      .then(results => {
        if (!cancelled) {
          setTenants(results.filter((t): t is Tenant => t !== null));
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentKey, clients]);

  return {tenants, loading, error};
}
